import {defaultTo} from 'lodash'
import {useState} from 'react'
import './text-input.scss'
import {Icon, TextInput, Tooltip} from '@/components/atoms'
import useTextInputValidation from '@/components/molecules/TextInputField/useTextInputValidation-hook'

/**
 * TextInputField component renders a text input field with various configurations.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.value - The current value of the input field.
 * @param {function} props.updateValue - Function to update the value of the input field.
 * @param {string} props.label - The label for the input field.
 * @param {Object} props.schema - The schema defining the input field type and validation.
 * @param {Object} props.uiSchema - The UI schema defining additional UI properties.
 * @param {boolean} [props.required=false] - Whether the input field is required.
 * @param {boolean} [props.disabled=false] - Whether the input field is disabled.
 * @param {boolean} props.visible - Whether the input field is visible.
 *
 * @returns {JSX.Element|null} The rendered TextInputField component or null if not visible.
 */
const TextInputField = ({value, updateValue, label, schema, uiSchema, required = false, disabled = false, visible}) => {
  const {onChange} = useTextInputValidation(label, uiSchema.validationRules)
  const [error, setError] = useState()
  const [isFocused, setIsFocused] = useState(false)

  const getUpdatedValue = (schema, val) => {
    if (schema.type === 'number' || schema.type === 'integer') {
      if (val) {
        return parseInt(val)
      }
    }
    return val
  }

  const getInputType = schema => {
    return schema?.type === 'integer' ? 'number' : schema?.type || 'text'
  }

  const handleOnChange = val => {
    const updatedValue = getUpdatedValue(schema, val)
    const error = onChange(val)
    setError(error)
    updateValue(updatedValue, error ? true : false)
  }

  const handleKeyDown = e => {
    const {key} = e
    if (schema.restrictDecimal) {
      if (['.', ',', 'e', '-'].includes(key) || (!value && key === '0')) {
        e.preventDefault()
      }
    } else if (
      (schema.type === 'number' && ['e', 'E', '+', '-'].includes(key)) ||
      ['ArrowUp', 'ArrowDown'].includes(key)
    ) {
      e.preventDefault()
    }
  }

  const disableButton = disabled || uiSchema?.readOnly ? 'disable-button' : ''
  return visible ? (
    <>
      <div className={`input-text-wrapper ${uiSchema?.className}`}>
        <TextInput
          required={required}
          className={`${uiSchema?.className ?? ''} ${disableButton} inputType-${getInputType(schema)}`}
          disabled={disabled || uiSchema?.readOnly || uiSchema?.disabled}
          maxLength={uiSchema?.maxLength ?? undefined}
          type={getInputType(schema)}
          error={error}
          label={label}
          onChange={handleOnChange}
          isDark={uiSchema?.isDark || false}
          value={defaultTo(value, undefined)}
          placeholderText={uiSchema?.placeholder ?? ''}
          helperText={uiSchema?.helperText}
          onBlur={() => {
            !isFocused && setIsFocused(true)
          }}
          handleKeyDown={handleKeyDown}
        />
        {uiSchema?.tooltipText && uiSchema?.tooltipText?.length > 0 && (
          <div className="tooltip-text">
            <Tooltip text={uiSchema.tooltipText}>
              <Icon alt="" icon="info" size="small" />
            </Tooltip>
          </div>
        )}
      </div>

      {!error && uiSchema.helpText && (
        <div className="input-help-text items-center">
          <Tooltip text={uiSchema.helpText} className="help-tooltip">
            <Icon icon="info" size="small" />
          </Tooltip>
          {uiSchema.helpText ? uiSchema.helpText : 'Help'}
        </div>
      )}
    </>
  ) : null
}

export default TextInputField
